import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo, faqList } from '@/mocks/site-info';

export default function Home() {
  const featuredArticles = articles.slice(0, 6);
  const latestNews = articles.filter((a) => a.category === 'news').slice(0, 3);

  const features = [
    {
      icon: 'ri-global-line',
      title: '官方入口直达',
      description: '提供官方最新访问地址，确保用户安全、快速地进入平台，避免钓鱼网站风险。',
    },
    {
      icon: 'ri-user-star-line',
      title: '一键注册登录',
      description: '支持手机号、邮箱等多种注册方式，短信验证码快速登录，账号管理便捷安全。',
    },
    {
      icon: 'ri-file-text-line',
      title: '详细使用教程',
      description: '从入门到精通，覆盖平台所有功能的详细操作指南，助您快速上手。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全防护保障',
      description: '多重安全机制保护用户数据和隐私，通过国家网络安全等级保护认证。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '7×24客服支持',
      description: '全天候在线客服团队，快速响应用户问题，专业高效的服务体验。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端同步使用',
      description: '支持PC、手机、平板多设备访问，数据实时同步，随时随地畅享服务。',
    },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: siteInfo.address,
    },
    foundingDate: siteInfo.foundingYear,
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteInfo.url}/categories?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="官方入口与核心功能全解析"
        description="51cg网是用户信赖的综合性网络平台，本页详细介绍51cg网的官方入口、核心功能、注册登录流程及使用技巧，帮助新老用户快速上手，充分享受51cg网带来的便捷服务体验。"
        keywords="51cg网,51cg网官方入口,51cg网注册登录,51cg网使用教程,51cg网最新地址,51cg网功能介绍"
        canonicalUrl="https://www.51cg.com"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20digital%20platform%20brand%20hero%20image%20with%20warm%20tones%2C%20clean%20minimalist%20design%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20elegant%20composition&width=1200&height=630&seq=home-og&orientation=landscape"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[550px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20warm%20golden%20and%20cream%20gradient%20background%2C%20modern%20minimalist%20design%2C%20soft%20flowing%20shapes%2C%20elegant%20artistic%20composition%2C%20luxurious%20texture%2C%20subtle%20organic%20curves%2C%20professional%20corporate%20atmosphere%2C%20high%20detail&width=1920&height=1080&seq=home-hero-bg&orientation=landscape"
            alt="51cg网平台背景"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
              51cg网 — 您的综合性网络服务平台
            </h1>
            <p className="text-background-50/85 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              官方入口直达 · 核心功能全覆盖 · 安全可靠 · 500万+用户信赖之选
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/categories"
                className="bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                探索功能
              </Link>
              <Link
                to="/about"
                className="bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3.5 rounded-md text-base font-medium whitespace-nowrap hover:bg-background-50/25 transition-colors cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '500万+', label: '累计服务用户' },
                { value: '98%', label: '用户满意度' },
                { value: '10+', label: '核心功能模块' },
                { value: '7×24h', label: '全天候客服支持' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <div className="text-foreground-500 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">核心功能介绍</h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">全面了解51cg网的核心功能，快速掌握平台使用技巧</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-lg p-6 border border-background-200/70 hover:border-primary-200 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                    <i className={`${feature.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">分类导航</h2>
              <p className="text-foreground-500 text-sm max-w-xl mx-auto">选择您感兴趣的分类，快速找到需要的内容</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/categories?tab=${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">最新文章</h2>
                <p className="text-foreground-500 text-sm">精选优质教程与资讯，助您高效使用51cg网</p>
              </div>
              <Link
                to="/categories"
                className="hidden sm:flex items-center gap-1 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
            <div className="mt-6 text-center sm:hidden">
              <Link
                to="/categories"
                className="text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors cursor-pointer inline-flex items-center gap-1"
              >
                查看全部文章 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest News */}
        <section className="py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">最新资讯</h2>
              <p className="text-foreground-500 text-sm">关注平台动态，获取第一手资讯</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-4">
              {latestNews.map((news) => (
                <Link
                  key={news.slug}
                  to={`/article/${news.slug}`}
                  className="flex items-center gap-5 p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Minimalist%20news%20announcement%20icon%20on%20warm%20cream%20background%2C%20simple%20geometric%20design%2C%20soft%20lighting%2C%20clean%20editorial%20style&width=120&height=120&seq=news-icon&orientation=squarish"
                      alt={news.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                      {news.title}
                    </h3>
                    <p className="text-foreground-500 text-sm mt-1 line-clamp-1">{news.excerpt}</p>
                    <span className="text-foreground-400 text-xs mt-2 inline-block">{news.date}</span>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 transition-colors text-lg flex-shrink-0"></i>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">常见问题</h2>
              <p className="text-foreground-500 text-sm">快速解答您的疑问，助您无忧使用</p>
            </div>
            <div className="space-y-3">
              {faqList.slice(0, 6).map((faq) => (
                <details key={faq.q} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-medium text-foreground-800 text-sm pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-500 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/categories?tab=faq"
                className="text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors cursor-pointer inline-flex items-center gap-1"
              >
                查看更多问题 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-20 relative overflow-hidden bg-secondary-500">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20flowing%20gradient%20background%20with%20warm%20golden%20and%20burgundy%20tones%2C%20modern%20minimalist%20design%2C%20soft%20organic%20waves%2C%20elegant%20artistic%20composition%2C%20professional%20corporate%20atmosphere&width=1920&height=600&seq=home-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">准备好开始了吗？</h2>
            <p className="text-background-50/75 text-sm md:text-base mb-8">立即注册51cg网账号，探索更多精彩功能，开启高效便捷的网络服务体验。</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#"
                className="bg-background-50 text-secondary-600 px-8 py-3.5 rounded-md text-base font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                立即注册
              </a>
              <Link
                to="/categories?tab=tutorials"
                className="bg-transparent text-background-50 border border-background-50/40 px-8 py-3.5 rounded-md text-base font-medium whitespace-nowrap hover:bg-background-50/10 transition-colors cursor-pointer"
              >
                查看教程
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}